import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { APPLY_CUSTOMER_ABONO_MUTATION } from '@/modules/open-accounts/open-accounts.gql';

import { PAYMENT_METHODS_QUERY } from '@/modules/cashier-pos/cashier-pos.gql';
import { formatCentsString, majorStringToCents } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface PaymentMethod {
  id: string;
  code: string;
  name: string;
  requires_reference: boolean;
}

interface PaymentMethodsResponse {
  paymentMethods: { items: PaymentMethod[] };
}

interface CustomerAbonoAllocation {
  open_account_id: string;
  pet_id: string;
  pet_name: string | null;
  amount_applied_cents: string;
  balance_before_cents: string;
  balance_after_cents: string;
  payment_id: string;
  ticket_id: string;
  ticket_folio_number: string | null;
}

interface ApplyCustomerAbonoResponse {
  applyCustomerAbono: {
    customer_id: string;
    total_amount_cents: string;
    allocations: CustomerAbonoAllocation[];
  };
}

interface ApplyCustomerAbonoDialogProps {
  customer_id: string;
  total_balance_cents: string;
  on_close: () => void;
  on_submitted: () => void | Promise<void>;
}

export function ApplyCustomerAbonoDialog({
  customer_id,
  total_balance_cents,
  on_close,
  on_submitted,
}: ApplyCustomerAbonoDialogProps): JSX.Element {
  const { t } = useTranslation();
  const { data: methods_data } = useQuery<PaymentMethodsResponse>(PAYMENT_METHODS_QUERY, {
    variables: { offset: 0, limit: 50 },
    fetchPolicy: 'cache-first',
  });
  const methods = (methods_data?.paymentMethods.items ?? []).filter(
    (m) => m.code !== 'open_account',
  );

  const [payment_method_id, setPaymentMethodId] = useState('');
  const [amount_major, setAmountMajor] = useState('');
  const [reference_number, setReferenceNumber] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ApplyCustomerAbonoResponse['applyCustomerAbono'] | null>(
    null,
  );

  useEffect(() => {
    if (!payment_method_id && methods.length > 0) {
      const cash = methods.find((m) => m.code === 'cash') ?? methods[0];
      if (cash) setPaymentMethodId(cash.id);
    }
  }, [methods, payment_method_id]);

  const selected_method = methods.find((m) => m.id === payment_method_id);
  const requires_reference = selected_method?.requires_reference ?? false;

  let amount_cents = '0';
  try {
    amount_cents = majorStringToCents(amount_major || '0').toString();
  } catch {
    amount_cents = '-1';
  }
  const amount_valid = /^\d+$/.test(amount_cents) && amount_cents !== '0';
  const missing_reference = requires_reference && reference_number.trim().length === 0;
  const balance_cents = safeBigInt(total_balance_cents);
  const exceeds_balance = amount_valid && BigInt(amount_cents) > balance_cents;

  const [applyCustomerAbono, { loading }] = useMutation<ApplyCustomerAbonoResponse>(
    APPLY_CUSTOMER_ABONO_MUTATION,
  );

  const can_submit =
    Boolean(payment_method_id) &&
    amount_valid &&
    !missing_reference &&
    !exceeds_balance &&
    !loading;

  const onClickSubmit = async (): Promise<void> => {
    setError(null);
    const idempotency_key =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      const response = await applyCustomerAbono({
        variables: {
          input: {
            customer_id,
            payment_method_id,
            amount_cents,
            reference_number: reference_number.trim() || null,
            note: note.trim() || null,
          },
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        context: { headers: { 'Idempotency-Key': idempotency_key } },
      });
      const payload = response.data?.applyCustomerAbono;
      if (payload) {
        setResult(payload);
        await on_submitted();
      }
    } catch (err) {
      if (err instanceof ApolloError) {
        const code = err.graphQLErrors[0]?.message ?? err.message;
        if (code.startsWith('customer_abono_exceeds_total_balance')) {
          setError(t('customer_statement.abono_error_exceeds_balance'));
          return;
        }
        if (code === 'customer_has_no_open_balance') {
          setError(t('customer_statement.abono_error_no_balance'));
          return;
        }
        if (code === 'cash_shift_required') {
          setError(t('customer_statement.abono_error_shift_required'));
          return;
        }
        if (code.startsWith('payment_reference_required')) {
          setError(t('customer_statement.abono_error_reference_required'));
          return;
        }
        setError(code);
        return;
      }
      if (err instanceof Error) setError(err.message);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>{t('customer_statement.abono_dialog_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {result ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t('customer_statement.abono_success', {
                  amount: formatCentsString(result.total_amount_cents),
                })}
              </p>
              <ul className="space-y-2">
                {result.allocations.map((allocation) => (
                  <li
                    key={allocation.open_account_id}
                    className="rounded-md border border-border bg-card/40 p-3 text-sm"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">
                        {allocation.pet_name ?? t('customer_statement.unknown_pet')}
                      </span>
                      <span className="font-mono">
                        {formatCentsString(allocation.amount_applied_cents)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('customer_statement.abono_balance_change', {
                        before: formatCentsString(allocation.balance_before_cents),
                        after: formatCentsString(allocation.balance_after_cents),
                      })}
                    </p>
                  </li>
                ))}
              </ul>
              <Button type="button" onClick={on_close}>
                {t('common.close')}
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-md border border-border bg-card/40 p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {t('customer_statement.pending_balance_label')}
                  </span>
                  <span className="font-mono">{formatCentsString(total_balance_cents)}</span>
                </div>
                {exceeds_balance ? (
                  <p className="mt-1 text-xs text-destructive">
                    {t('customer_statement.abono_error_exceeds_balance')}
                  </p>
                ) : null}
              </div>

              <div className="space-y-1">
                <Label>{t('customer_statement.payment_method_label')}</Label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                  value={payment_method_id}
                  onChange={(e) => setPaymentMethodId(e.target.value)}
                  disabled={loading}
                >
                  {methods.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label>{t('customer_statement.amount_label')}</Label>
                  <Input
                    inputMode="decimal"
                    className="font-mono"
                    value={amount_major}
                    onChange={(e) => setAmountMajor(e.target.value)}
                    disabled={loading}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-1">
                  <Label>
                    {t('customer_statement.reference_label')}
                    {requires_reference ? ' *' : ''}
                  </Label>
                  <Input
                    value={reference_number}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    disabled={loading || !requires_reference}
                    placeholder={requires_reference ? '...' : '-'}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="customer-abono-note">{t('customer_statement.note_label')}</Label>
                <Input
                  id="customer-abono-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={loading}
                />
              </div>

              {error ? <p className="text-sm text-destructive">{error}</p> : null}
            </>
          )}
        </CardContent>
        {!result ? (
          <CardFooter className="justify-end gap-2">
            <Button type="button" variant="ghost" onClick={on_close} disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => {
                void onClickSubmit();
              }}
              disabled={!can_submit}
            >
              {loading ? t('common.loading') : t('customer_statement.abono_submit')}
            </Button>
          </CardFooter>
        ) : null}
      </Card>
    </div>
  );
}

function safeBigInt(value: string): bigint {
  try {
    return /^-?\d+$/.test(value) ? BigInt(value) : 0n;
  } catch {
    return 0n;
  }
}
